import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  MinusIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading.tsx";
import { ButtonColor, ModalSize } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import {
  type FC,
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
} from "react";

interface ModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  id?: string;
  title?: React.ReactNode;
  subTitle?: string;
  /**
   * Runs when Confirm is pressed. Resolve `false` to KEEP THE MODAL OPEN, for a confirm that did
   * not go through: a failed validation, or a caller that decided not to proceed. Resolving
   * `true` or `undefined` closes it, so a caller written before this contract existed still
   * behaves the way it always did. A rejection also keeps it open.
   *
   * ⚠️ react-hook-form's `handleSubmit(fn)` does NOT throw on a failed validation. It skips `fn`,
   * sets the field errors and resolves, so handing it here raw closes the modal on an invalid
   * submit and destroys both the user's input and the errors just rendered. Resolve `false` on
   * the invalid branch. (#450)
   */
  onConfirm?: () => Promise<void | boolean>;
  onCancel?: () => void;
  confirmText?: string;
  confirmButtonColor?: ButtonColor;
  showButtons?: boolean;
  cancelText?: string;
  size?: string;
  height?: number;
  minHeight?: number;
  width?: number;
  hasCancelButton?: boolean;
  isConfirmLoading?: boolean;
  isConfirmDisabled?: boolean;
  isLoading?: boolean;
  children?: ReactNode;
  disableClose?: boolean;
  bgOpacity?: number;
  isMinimized?: boolean;
  onMinimize?: () => void;
  onRestore?: () => void;
  showMinimizeButton?: boolean;
  minimizedHighlight?: boolean;
  /** Callback to intercept close attempts. Return false to prevent closing. */
  onBeforeClose?: () => boolean | Promise<boolean>;
}

const Modal: FC<ModalProps> = ({
  open,
  setOpen,
  id,
  title,
  subTitle,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  confirmButtonColor = ButtonColor.Default,
  showButtons = true,
  cancelText = "Close",
  size = ModalSize.Small,
  height,
  minHeight,
  width,
  hasCancelButton = true,
  isConfirmLoading = false,
  isConfirmDisabled = false,
  isLoading = false,
  children,
  disableClose = false,
  bgOpacity = 75,
  isMinimized = false,
  onMinimize,
  onRestore,
  showMinimizeButton = true,
  minimizedHighlight = false,
  onBeforeClose,
}) => {
  const modalSize = {
    [ModalSize.Small]: "min-w-96",
    [ModalSize.Large]: "min-w-[600px]",
    [ModalSize.XLarge]: "min-w-[1200px]",
  };

  const modalStyle = {
    ...(height ? { height: `${height}px` } : {}),
    ...(minHeight ? { minHeight: `${minHeight}px` } : {}),
    ...(width ? { width: `${width}px` } : {}),
  };

  // Define a mapping for button color classes
  const buttonColorClasses = {
    [ButtonColor.Default]:
      "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600",
    [ButtonColor.Red]:
      "bg-red-600 hover:bg-red-500 focus-visible:outline-red-600",
    [ButtonColor.Gray]:
      "bg-gray-600 hover:bg-gray-500 focus-visible:outline-gray-600",
    [ButtonColor.Green]:
      "bg-green-600 hover:bg-green-500 focus-visible:outline-green-600",
  };

  // Close the modal if the user hits the escape key
  const handleKeyDown = async (
    e: KeyboardEvent<HTMLDivElement>
  ): Promise<void> => {
    if (e.key === "Escape" && !isConfirmLoading && !isMinimized) {
      if (onBeforeClose) {
        const shouldClose = await Promise.resolve(onBeforeClose());
        if (!shouldClose) {
          return; // Prevent closing
        }
      }
      setOpen(false);
      onCancel?.();
    }
  };

  const handleConfirm = (): void => {
    if (isConfirmLoading || !onConfirm) {
      return;
    }

    void Promise.resolve(onConfirm())
      .then(confirmed => {
        // Only an explicit false keeps it open. Undefined has to keep closing the modal: every
        // caller returned void when this contract was introduced, and treating that as "did not
        // succeed" would have wedged every modal in the product.
        if (confirmed !== false) {
          setOpen(false);
        }
      })
      .catch(() => {
        // A rejected confirm keeps the modal open, which is what the user needs: their input and
        // whatever the caller rendered about the failure are both still on screen. Swallowed on
        // purpose, because reporting the failure belongs to the caller, and without this the
        // rejection was merely unhandled rather than handled.
      });
  };

  const handleCancel = async (): Promise<void> => {
    if (isConfirmLoading || disableClose) {
      return;
    }

    // Check if we should intercept the close
    if (onBeforeClose) {
      const shouldClose = await Promise.resolve(onBeforeClose());
      if (!shouldClose) {
        return; // Prevent closing
      }
    }

    setOpen(false);
    onCancel?.();
  };

  const modalId =
    "modal-" +
    (id
      ? id
      : (typeof title === "string" ? title : "no-topic")
          .toLowerCase()
          .split(" ")
          .join("-"));

  // Reset minimized state when modal is closed externally
  useEffect(() => {
    if (!open && isMinimized && onRestore) {
      onRestore();
    }
  }, [open, isMinimized, onRestore]);

  return (
    <>
      {/* When minimized, render without Dialog wrapper to allow background interaction */}
      {isMinimized && (
        <div className="relative z-[99]">
          <div
            id={modalId}
            className="pointer-events-none fixed bottom-0 left-0 right-4 flex justify-end">
            <div
              className={classNames(
                "pointer-events-auto h-16 max-h-16 transition-all duration-300 ease-in-out shadow-xl border-t rounded-t-lg px-4 sm:px-6",
                minimizedHighlight
                  ? "bg-indigo-300 ring-2 ring-indigo-300 animate-pulse border-indigo-300"
                  : "bg-white border-gray-200"
              )}>
              <div className="flex h-full items-center justify-between gap-4">
                {/* Title */}
                {title && (
                  <div className="text-xs sm:text-sm font-medium text-gray-700 truncate max-w-[150px] sm:max-w-[200px]">
                    {typeof title === "string" ? title : "Modal"}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  {onRestore && (
                    <button
                      type="button"
                      className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none p-2"
                      onClick={onRestore}
                      title="Restore modal">
                      <span className="absolute -inset-2.5" />
                      <span className="sr-only">Restore modal</span>
                      <ChevronRightIcon
                        className="h-5 w-5 -rotate-90"
                        aria-hidden="true"
                      />
                    </button>
                  )}
                  <button
                    type="button"
                    className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none p-2"
                    onClick={handleCancel}
                    title="Close modal">
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Close modal</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Regular modal when not minimized */}
      <Transition.Root show={open && !isMinimized} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100]"
          onKeyDown={handleKeyDown}
          onClose={() => {
            handleCancel();
          }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div
              className={classNames(
                "fixed inset-0 bg-gray-500 transition-opacity",
                `bg-opacity-${bgOpacity ?? 75}`
              )}
            />
          </Transition.Child>

          <div className="fixed inset-0 z-50 w-screen overflow-x-hidden overflow-y-auto overscroll-contain">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel
                  id={modalId}
                  style={modalStyle}
                  className={classNames(
                    modalSize[size],
                    "flex flex-col relative overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-auto sm:p-6"
                  )}>
                  {disableClose ? null : (
                    <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                      <div className="flex items-center gap-2">
                        {showMinimizeButton && onMinimize && (
                          <button
                            type="button"
                            className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                            onClick={onMinimize}
                            title="Minimize modal">
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Minimize modal</span>
                            <MinusIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                          onClick={handleCancel}>
                          <span className="sr-only">Close</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  )}

                  {typeof title === "string" ? (
                    <div className="modal-header mb-4">
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {title}
                      </h3>
                      {subTitle && (
                        <p className="mt-1 text-xs leading-6 text-gray-500">
                          {subTitle}
                        </p>
                      )}
                    </div>
                  ) : (
                    title
                  )}
                  <div className="modal-body h-full">
                    {isLoading ? (
                      <div className="grid grid-cols-2 gap-6 py-6 sm:grid-cols-3 lg:grid-cols-4">
                        <SkeletonLoader count={12} />
                      </div>
                    ) : (
                      children
                    )}
                  </div>
                  {showButtons && (
                    <div className="modal-footer mt-auto flex justify-end space-x-2">
                      {onConfirm && (
                        <button
                          id={`${modalId}-${confirmText ? confirmText.toLowerCase().split(" ").join("-") : "no-confirm"}`}
                          data-testid="modalConfirmButton"
                          type="button"
                          disabled={isConfirmLoading || isConfirmDisabled}
                          className={classNames(
                            "via-modal inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                            buttonColorClasses[confirmButtonColor]
                          )}
                          onClick={handleConfirm}>
                          <Spinner loading={isConfirmLoading}>
                            {confirmText}
                          </Spinner>
                        </button>
                      )}
                      {(onCancel ?? hasCancelButton) && (
                        <button
                          id={`${modalId}-${cancelText ? cancelText.toLowerCase().split(" ").join("-") : "no-cancel"}`}
                          data-testid="modalCancelButton"
                          type="button"
                          disabled={isConfirmLoading}
                          className={classNames(
                            "inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                            buttonColorClasses[ButtonColor.Gray]
                          )}
                          onClick={handleCancel}>
                          {cancelText}
                        </button>
                      )}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default Modal;
